import { Injectable } from '@angular/core';
import { PaginatedState, PaginatedStateModel } from '@shared/ui';
import { PipelineEnvironment } from '@telespot/domain';

export interface PipelineData {
  id: string;
  /**
   * Multilingual name
   */
  name: { [key: string]: string };
  /**
   * Environment of the pipeline
   */
  env: PipelineEnvironment;
  /**
   * Drives ordering and cursor pagination (newest first)
   */
  createdAt: Date;
  /**
   * Count of method types using the pipeline
   * Undefined value implies it is loading
   */
  usedByMethodTypes?: number;
  /**
   * Count of steps in the pipeline
   * Undefined value implies it is loading
   */
  numSteps?: number;
  /**
   * True for pipelines created in the UI but not yet persisted
   */
  pending?: boolean;
}

export interface PipelineStateModel extends PaginatedStateModel<PipelineData> {
  /**
   * Filter the listed pipelines by environment.
   * Undefined for no filter.
   */
  environmentFilter?: PipelineEnvironment;
}

@Injectable()
export class PipelineState extends PaginatedState<PipelineData, PipelineStateModel> {
  constructor() {
    super({ maxConcurrentCountQueries: 2 }, { environmentFilter: undefined });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly environmentFilter$ = this.select((state) => state.environmentFilter);

  // Backwards-compat alias selectors
  public readonly pipelines$ = this.items$;
  public readonly nextPipelineToken$ = this.nextToken$;
  public readonly previousPipelineTokens$ = this.previousTokens$;
  public readonly pipelinesLoaded$ = this.itemsLoaded$;
  public readonly noPipelines$ = this.noItems$;

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setEnvironmentFilter = this.updater((state, environmentFilter: PipelineEnvironment | undefined) => ({
    ...state,
    environmentFilter,
    hasMoreItems: true,
    previousPageTokens: [],
    nextToken: undefined,
    searching: true,
  }));
}
